import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Breadcrumbs, Grid, Hidden, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import HomeIcon from '@material-ui/icons/Home';
import { primaryColor, secondaryColor } from '../../assets/jss/nextjs-material-kit-pro';
import TitleBarContext from './TitleBarContext';

const breadcrumbText = {
  fontSize: '0.80rem',
};

const useStyles = makeStyles(() => ({
  titlebar: (props) => ({
    backgroundColor: '#fff',
    padding: props && props.spacing === 'none' ? '15px 5px 5px 10px' : '30px 10px 5px 10px',
    margin: props && props.spacing === 'none' ? '0' : '-15px 0 15px 0',
    borderBottom: '1px inset #f0f0f0',
  }),
  title: {
    fontSize: '1.75rem',
    fontWeight: 600,
  },
  breadcrumb: {
    padding: '18px 0 0 10px',
  },
  breadCrumbText: breadcrumbText,
  breadCrumbCurrentText: {
    ...breadcrumbText,
    color: secondaryColor,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
  },
  linkButton: {
    fontSize: '0.80rem',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    margin: 0,
    padding: 0,
    whiteSpace: 'nowrap',
    '&:hover,&:focus': {
      textDecoration: 'underline',
      color: primaryColor[0],
    },
  },
  unprintedContent: {
    '@media print': {
      display: 'none',
    },
    '@media screen': {},
  },
  addlInfo: {
    fontSize: '0.95rem',
    fontWeight: 400,
    marginTop: 5,
  },
  homeBreadCrumb: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  homeBreadCrumbIcon: {
    fontSize: '1.30rem',
  },
  homeBreadCrumbLabel: {
    margin: '1px 0 0 10px',
  },
}));

const TitleBar = (props) => {
  const { spacing, noMargin } = props;
  const contextSpacingOrBool = useContext(TitleBarContext);
  // Determine effective spacing with backward compatibility:
  // 1) use spacing prop if provided
  // 2) else if legacy noMargin boolean provided, map to spacing
  // 3) else if context supplies a string use it, otherwise map boolean context to spacing
  let effectiveSpacing;
  if (typeof spacing !== 'undefined') {
    effectiveSpacing = spacing;
  } else if (typeof noMargin !== 'undefined') {
    effectiveSpacing = noMargin ? 'none' : 'default';
  } else if (typeof contextSpacingOrBool === 'string') {
    effectiveSpacing = contextSpacingOrBool;
  } else {
    effectiveSpacing = contextSpacingOrBool ? 'none' : 'default';
  }
  const classes = useStyles({ spacing: effectiveSpacing });
  const { title, currentPage, paths, additionalInformation } = props;
  const history = useHistory();

  return (
    <Grid container className={classes.titlebar}>
      <Grid item xs={12} md={12} lg={12} className={classes.unprintedContent}>
        <Hidden smDown implementation='css'>
          <section className={classes.breadcrumb}>
            <Breadcrumbs
              separator='›'
              aria-label='breadcrumb'
              className={classes.breadCrumbText}
              maxItems={100}
              style={{ whiteSpace: 'nowrap', flexWrap: 'nowrap', overflowX: 'auto', display: 'flex', alignItems: 'center' }}
            >
              {paths.map((p, i) => {
                return (
                  <button
                    key={i}
                    color='inherit'
                    className={[classes.breadCrumbText, classes.linkButton].join(' ')}
                    onClick={() => (p.onClickFunc ? p.onClickFunc() : history.push(p.path))}
                  >
                    {p.name === 'Home' ? (
                      <span className={classes.homeBreadCrumb}>
                        <HomeIcon className={classes.homeBreadCrumbIcon} />
                        <span className={classes.homeBreadCrumbLabel}>{p.name}</span>
                      </span>
                    ) : (
                      p.name
                    )}
                  </button>
                );
              })}
              {currentPage && (
                <Typography component='span' color='textPrimary' className={classes.breadCrumbCurrentText}>
                  {currentPage}
                </Typography>
              )}
            </Breadcrumbs>
          </section>
        </Hidden>
      </Grid>
      {title && (
        <Grid item xs={12} md={12} lg={12}>
          <section className={classes.title}>{title}</section>
        </Grid>
      )}
      <Grid item xs={false} md={12} lg={12}>
        <section className={classes.addlInfo}>{additionalInformation}</section>
      </Grid>
    </Grid>
  );
};

TitleBar.propTypes = {
  title: PropTypes.string,
  currentPage: PropTypes.string.isRequired,
  paths: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      name: PropTypes.string,
      onClickFunc: PropTypes.func,
    })
  ).isRequired,
  additionalInformation: PropTypes.string,
  showHomeIconForHomePath: PropTypes.bool,
  // spacing controls both padding and margin. Values: 'none' | 'compact' | 'default'
  spacing: PropTypes.oneOf(['none', 'compact', 'default']),
  // deprecated: legacy boolean. Keep for compatibility.
  noMargin: PropTypes.bool,
};

export default TitleBar;
